import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Select, FormControl,
} from '@mui/material';
import { ClinicDialog } from '../dialogs/ClinicDialog';
import { CLINIC_DIALOG_SX } from '../../constants/popupStyles';
import { wellnessItemService, WellnessItem } from '../../services/wellnessItemService';
import { WellnessCategory } from '../../types/wellnessCategory';

interface Props {
  open: boolean;
  onClose: () => void;
  editingCategory: WellnessCategory | null;
  wellnessItems: WellnessItem[];
  onSave: (category: WellnessCategory) => void;
}

const WellnessCategoryDialog: React.FC<Props> = ({ open, onClose, editingCategory, wellnessItems, onSave }) => {
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [wellnessItemId, setWellnessItemId] = useState<number | ''>('');
  const [pictureFileId, setPictureFileId] = useState<number | null>(null);
  const [picturePreviewUrl, setPicturePreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [titleError, setTitleError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (editingCategory) {
      setTitle(editingCategory.title);
      setSubtitle(editingCategory.subtitle);
      setWellnessItemId(editingCategory.wellness_item_id ?? '');
      setPictureFileId(editingCategory.picture_file_id);
      setPicturePreviewUrl(null);
      if (editingCategory.picture_file_id) {
        wellnessItemService.getPictureBlobUrl(editingCategory.picture_file_id)
          .then(setPicturePreviewUrl)
          .catch(() => {});
      }
    } else {
      setTitle('');
      setSubtitle('');
      setWellnessItemId('');
      setPictureFileId(null);
      setPicturePreviewUrl(null);
    }
    setTitleError('');
  }, [open, editingCategory]);

  const handlePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { file_id } = await wellnessItemService.uploadPicture(file);
      setPictureFileId(file_id);
      setPicturePreviewUrl(URL.createObjectURL(file));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setTitleError('Title is required'); return; }
    onSave({
      _key: editingCategory?._key ?? Math.random().toString(36).slice(2),
      title: title.trim(),
      subtitle: subtitle.trim(),
      picture_file_id: pictureFileId,
      wellness_item_id: wellnessItemId !== '' ? Number(wellnessItemId) : null,
    });
  };

  return (
    <ClinicDialog
      open={open}
      onClose={onClose}
      title={editingCategory ? 'Edit Category' : 'Add Category'}
      closeButtonSide="left"
      onSubmit={handleSubmit}
      actions={
        <>
          <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
            {editingCategory ? 'Update' : 'Add'}
          </Button>
          <Button onClick={onClose} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 360 }}>
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Title</Typography>
          <TextField
            value={title}
            onChange={(e) => { setTitle(e.target.value); if (e.target.value.trim()) setTitleError(''); }}
            fullWidth size="small"
            error={!!titleError}
            helperText={titleError}
            inputProps={{ dir: 'rtl' }}
            InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
          />
        </Box>
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Subtitle</Typography>
          <TextField
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            fullWidth size="small"
            inputProps={{ dir: 'rtl' }}
            InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
          />
        </Box>
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Wellness Item</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={wellnessItemId}
              onChange={(e) => {
                const id = e.target.value as number | '';
                setWellnessItemId(id);
                const selected = wellnessItems.find((w) => w.id === id);
                if (selected) {
                  setTitle(selected.data?.title || '');
                  setSubtitle(selected.data?.subtitle || '');
                }
              }}
              displayEmpty
              sx={{ background: (CLINIC_DIALOG_SX.textField as any).background }}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {wellnessItems.map((w) => (
                <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Picture</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
            {picturePreviewUrl && (
              <Box component="img" src={picturePreviewUrl} alt="preview"
                sx={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
            )}
            <Button component="label" variant="outlined" size="small" disabled={uploading}>
              {uploading ? 'Uploading...' : picturePreviewUrl ? 'Change Picture' : 'Upload Picture'}
              <input type="file" hidden accept="image/*" onChange={handlePictureChange} />
            </Button>
          </Box>
        </Box>
      </Box>
    </ClinicDialog>
  );
};

export default WellnessCategoryDialog;
